import React, { useState, useEffect, useMemo } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import Table from "../../../../components/ReactTable/Table";
import { useTableData } from "../../../../components/ReactTable/useTableData";
import { useDispatch } from "react-redux";
import {
  enqueueErrorSnackbar,
  enqueueSuccessSnackbar
} from "../../../../redux/actions/snackbarActions";
import { useVirtualClassrooms } from "../../../../hooks/virtualClassrooms";
import { statusTypes, courseStatuses } from "../../../../constants/index";
import TextFilter from "../../../../components/ReactTable/Filters/TextFilter";
import StatusFilter from "./StatusFilter";

const filterByStatus = statusId => virtualClassroom =>
  !statusId ? true : virtualClassroom.statusType.id === statusId;

const columns = (t, onResendActivationClick) => [
  {
    Header: t("table.columnHeader.name"),
    accessor: "name",
    id: "name",
    Filter: TextFilter,
    filterPlaceholder: t("table.filterPlaceholder.nameAll")
  },
  {
    accessor: "activated",
    Cell: ({ row: { id }, cell: { value: activated } }) =>
      !activated ? (
        <Box my={-1}>
          <Button
            color="primary"
            variant="text"
            onClick={() => onResendActivationClick(id)}
            data-qa="resend-activation-email-btn"
          >
            {t("manageCourses.resendActivationEmail")}
          </Button>
        </Box>
      ) : null,
    disableSortBy: true,
    id: "action"
  },
  {
    Header: t("table.columnHeader.activationStatus"),
    accessor: "activated",
    Cell: ({ cell: { value: activated } }) => (
      <Typography
        variant="body2"
        color={activated ? "textPrimary" : "textSecondary"}
      >
        {activated
          ? t("manageCourses.virtualClassroomActivated")
          : t("manageCourses.virtualClassroomNotActivated")}
      </Typography>
    ),
    id: "activationStatus"
  },
  {
    Header: t("table.columnHeader.status"),
    accessor: "statusType.id",
    Cell: ({ cell: { value: status } }) => (
      <Typography variant="body2">
        {status === statusTypes.active
          ? t("manageCourses.virtualClassroomActive")
          : t("manageCourses.virtualClassroomInactive")}
      </Typography>
    ),
    id: "status"
  }
];

const VirtualClassroomsTable = () => {
  const dispatch = useDispatch();
  const { getVirtualClassrooms, sendReactivation } = useVirtualClassrooms();
  const { data, isFetching } = useTableData(getVirtualClassrooms);
  const { t } = useTranslation();

  const [statusFilter, setStatusFilter] = useState(courseStatuses[1].id);
  const [virtualClassrooms, setVirtualClassrooms] = useState([]);

  useEffect(() => {
    setVirtualClassrooms(data.filter(filterByStatus(statusFilter)));
  }, [data, statusFilter]);

  const handleResendActivation = virtualClassroomId => {
    return sendReactivation(virtualClassroomId)
      .then(() => {
        dispatch(
          enqueueSuccessSnackbar({
            message: t("manageCourses.resendEmailSuccess")
          })
        );
      })
      .catch(() =>
        dispatch(
          enqueueErrorSnackbar({
            message: t("manageCourses.resendEmailError")
          })
        )
      );
  };

  const memoColumns = useMemo(() => columns(t, handleResendActivation), []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      mt={3}
      data-qa="virtual-classroom-table-container"
    >
      <Box mb={3}>
        <StatusFilter
          onChange={setStatusFilter}
          defaultValue={courseStatuses[1].id}
        />
      </Box>
      <Table
        data={virtualClassrooms}
        columns={memoColumns}
        paginated={virtualClassrooms.length > 10}
        manualPagination={false}
        rowsPerPageOptions={[10, 15, 20]}
        manualFilters={false}
        manualSortBy={false}
        loading={isFetching}
        cellSize="medium"
      />
    </Box>
  );
};

export default VirtualClassroomsTable;
